/**
 * Multi-Vendor Shipping Module Dashboard
 * 
 * Admin dashboard for managing the hybrid shipping architecture:
 * - Phase 1 (INTERCEPT): Cart interception for Basic Shopify Plan
 * - Phase 2 (NATIVE): Carrier Service API for Advanced Shopify Plan
 * 
 * Features:
 * - Sales monitoring with upgrade alerts (₹3.6L Grow, ₹34L Advanced)
 * - Mode toggle (INTERCEPT ↔ NATIVE)
 * - Shopify connection status
 * - Flagged orders from Post-Purchase Auditor
 */

import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertCircle,
  CheckCircle2,
  Settings,
  TrendingUp,
  Package,
  Truck,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Zap,
  Target,
  ShieldCheck,
  XCircle
} from 'lucide-react';

import AdminHyperlocalCategories from '../../components/shipping/AdminHyperlocalCategories';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShippingModuleDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [flaggedOrders, setFlaggedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/shipping/admin/dashboard-widget`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch dashboard data');
      
      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const fetchFlaggedOrders = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/shipping/admin/flagged-orders?reviewed=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFlaggedOrders(data.flagged_orders || []);
      }
    } catch (err) {
      console.error('Failed to fetch flagged orders:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboardData(), fetchFlaggedOrders()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboardData, fetchFlaggedOrders]);

  const handleToggleMode = async () => {
    if (!dashboardData) return;
    
    const newMode = dashboardData.shipping_mode.current === 'INTERCEPT' ? 'NATIVE' : 'INTERCEPT';
    
    if (newMode === 'NATIVE' && !dashboardData.shipping_mode.can_switch_to_native) {
      alert('Cannot switch to NATIVE mode. Please ensure Shopify is connected with required scopes.');
      return;
    }
    
    setActionLoading('toggle');
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/shipping/admin/toggle-mode`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mode: newMode })
      });
      
      if (!response.ok) throw new Error('Failed to toggle mode');
      
      await fetchDashboardData();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetupLogisticsProduct = async () => {
    setActionLoading('logistics');
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/shipping/admin/setup-logistics-product`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to setup logistics product');
      
      await fetchDashboardData();
      alert('Logistics Fee product created successfully!');
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAutoHeal = async () => {
    setActionLoading('heal');
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/shipping/admin/auto-heal-logistics-product`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Auto-heal failed');
      
      await fetchDashboardData();
      alert(`Auto-heal complete: ${data.message}`);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolveFlag = async (orderId) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/shipping/admin/resolve-flag/${orderId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'acknowledged', notes: 'Reviewed by admin' })
      });
      
      if (!response.ok) throw new Error('Failed to resolve flag');
      
      await fetchFlaggedOrders();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const { shipping_mode, shopify_status, phase1_ready, phase2_ready, sales_monitoring, config } = dashboardData || {};

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Multi-Vendor Shipping Module</h1>
            <p className="text-muted-foreground">Manage your hybrid shipping architecture</p>
          </div>
          <Button variant="outline" onClick={() => { fetchDashboardData(); fetchFlaggedOrders(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Upgrade Alerts */}
        {sales_monitoring?.alerts?.map((alert, index) => (
        <Alert key={alert.id || alert.name || `${alert.title || "alert"}-${index}`} variant={alert.priority === 'critical' ? 'destructive' : 'default'} className="border-l-4 border-l-yellow-500">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="flex items-center gap-2">
            <span>{alert.icon}</span>
            {alert.title}
          </AlertTitle>
          <AlertDescription className="mt-2">
            <p>{alert.message}</p>
            {alert.estimated_monthly_savings && (
              <p className="font-semibold mt-1">
                Estimated Monthly Savings: {alert.formatted_savings}
              </p>
            )}
            {alert.steps && (
              <ol className="mt-2 ml-4 list-decimal text-sm">
                {alert.steps.map((step, i) => (
                  <li key={step.id || step.name || `${step.title || "step"}-${i}`}>{step.replace(/^\d+\.\s*/, '')}</li>
                ))}
              </ol>
            )}
            {alert.action?.url && (
              <Button size="sm" className="mt-3" asChild>
                <a href={alert.action.url} target="_blank" rel="noopener noreferrer">
                  {alert.action.label}
                  <ExternalLink className="h-3 w-3 ml-2" />
                </a>
              </Button>
            )}
          </AlertDescription>
        </Alert>
      ))}

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Monthly Sales Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Monthly Gross Sales</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              {sales_monitoring?.formatted_sales || '₹0'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {sales_monitoring?.order_count || 0} orders this month
            </p>
            {sales_monitoring?.progress && (
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span>To {sales_monitoring.progress.next_milestone}</span>
                  <span>{sales_monitoring.progress.progress_percentage}%</span>
                </div>
                <Progress value={sales_monitoring.progress.progress_percentage} className="h-2" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Mode Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Shipping Mode</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              {shipping_mode?.current === 'NATIVE' ? (
                <Zap className="h-5 w-5 text-yellow-500" />
              ) : (
                <Package className="h-5 w-5 text-blue-500" />
              )}
              {shipping_mode?.current || 'INTERCEPT'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant={shipping_mode?.current === 'NATIVE' ? 'default' : 'secondary'}>
              {shipping_mode?.current === 'NATIVE' ? 'Phase 2 Active' : 'Phase 1 Active'}
            </Badge>
          </CardContent>
        </Card>

        {/* Shopify Status Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Shopify Connection</CardDescription>
            <CardTitle className="text-lg flex items-center gap-2">
              {shopify_status?.connected ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              {shopify_status?.connected ? 'Connected' : 'Not Connected'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground truncate">
              {shopify_status?.shop || 'No shop connected'}
            </p>
            {shopify_status?.missing_scopes?.length > 0 && (
              <Badge variant="destructive" className="mt-2">
                Missing Scopes
              </Badge>
            )}
          </CardContent>
        </Card>

        {/* Provider Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Shipping Provider</CardDescription>
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="h-5 w-5 text-purple-500" />
              {config?.provider === 'mock' ? 'Mock Provider' : 'Shiprocket'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Margin: {config?.margin_percentage}% | Fallback: ₹{config?.flat_rate_fallback}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different sections */}
      <Tabs defaultValue="setup" className="w-full">
        <TabsList>
          <TabsTrigger value="setup">Setup & Configuration</TabsTrigger>
          <TabsTrigger value="auditor">
            Post-Purchase Auditor
            {flaggedOrders.length > 0 && (
              <Badge variant="destructive" className="ml-2">{flaggedOrders.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="setup" className="space-y-4 mt-4">
          {/* Phase Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Phase 1 Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Phase 1: INTERCEPT Mode
                </CardTitle>
                <CardDescription>
                  For Shopify Basic/Grow plans. Uses cart interception with popup modal.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Logistics Fee Product</span>
                  {phase1_ready?.status ? (
                    <Badge variant="success" className="bg-green-100 text-green-800">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Ready
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Not Created</Badge>
                  )}
                </div>
                
                {!phase1_ready?.status ? (
                  <Button 
                    className="w-full" 
                    onClick={handleSetupLogisticsProduct}
                    disabled={actionLoading === 'logistics'}
                  >
                    {actionLoading === 'logistics' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Settings className="h-4 w-4 mr-2" />
                    )}
                    Create Logistics Fee Product
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={handleAutoHeal}
                    disabled={actionLoading === 'heal'}
                  >
                    {actionLoading === 'heal' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ShieldCheck className="h-4 w-4 mr-2" />
                    )}
                    Run Auto-Heal Check
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Phase 2 Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Phase 2: NATIVE Mode
                </CardTitle>
                <CardDescription>
                  For Shopify Advanced plan. Uses native Carrier Service API.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Carrier Service</span>
                  {phase2_ready?.status ? (
                    <Badge variant="success" className="bg-green-100 text-green-800">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Registered
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Not Active</Badge>
                  )}
                </div>

                {shopify_status?.missing_scopes?.length > 0 && (
                  <Alert variant="destructive" className="py-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Missing scopes: {shopify_status.missing_scopes.join(', ')}. 
                      Please reinstall the Shopify app.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">Enable Native Shipping</p>
                    <p className="text-xs text-muted-foreground">
                      Switch from INTERCEPT to NATIVE mode
                    </p>
                  </div>
                  <Switch
                    checked={shipping_mode?.current === 'NATIVE'}
                    onCheckedChange={handleToggleMode}
                    disabled={!shipping_mode?.can_switch_to_native || actionLoading === 'toggle'}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Configuration Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Platform Margin</p>
                  <p className="text-xl font-semibold">{config?.margin_percentage}%</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Fallback Rate</p>
                  <p className="text-xl font-semibold">₹{config?.flat_rate_fallback}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Provider</p>
                  <p className="text-xl font-semibold capitalize">{config?.provider}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="auditor" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Flagged Orders (Post-Purchase Auditor)
              </CardTitle>
              <CardDescription>
                Orders with shipping address mismatches that may have incorrect shipping charges.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {flaggedOrders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p>No flagged orders. All shipping charges are verified!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {flaggedOrders.map((order) => (
                    <div 
                      key={order.order_id} 
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div>
                        <p className="font-medium">Order #{order.order_number}</p>
                        <p className="text-sm text-muted-foreground">
                          Quoted: {order.quoted_pincode} → Actual: {order.actual_pincode}
                        </p>
                        <p className="text-sm text-red-600">
                          Cost Difference: ₹{order.cost_difference?.toFixed(2)}
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleResolveFlag(order.order_id)}
                      >
                        Mark Resolved
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Hyperlocal Category Controls */}
      <AdminHyperlocalCategories />

      </div>
    </DashboardLayout>
  );
};

export default ShippingModuleDashboard;
